import { Queries } from '@aline/common';
import type { AlineEditor, ImageMap, UUID, WithContext } from '@aline/common';
import { DocxSerializer } from '@aline/common/serializers/docx/index';
import { Packer } from 'docx';
import { saveAs } from 'file-saver';
import sanitize from 'sanitize-filename';
import type { Apollo } from 'src/gql/generated';
import { setFileExtension } from 'src/helpers/utils';

import { ClientTransforms } from '.';

export const documentTransforms = {
  /**
   * Serialize editor.children to .docx format and save.
   */
  exportToDocx: async (
    editor: AlineEditor,
    options: {
      dbComments: DocxSerializer.DBComment[];
      documentAuthors: Pick<Apollo.User, 'displayName' | 'id' | '__typename'>[];
      documentId: UUID;
      imageMap: ImageMap;
      name: string;
    }
  ): Promise<void> => {
    const { name } = options;
    const { value: blob } = await ClientTransforms.getDocxBlobDataFromJson(editor, options);

    saveAs(blob, sanitize(setFileExtension(name, 'docx')));
  },

  /**
   * Takes the Document JSON (DocParts) and returns docx blob
   */
  async getDocxBlobDataFromJson(
    editor: AlineEditor,
    options: {
      dbComments: DocxSerializer.DBComment[];
      documentAuthors: Pick<Apollo.User, 'displayName' | 'id' | '__typename'>[];
      documentId: UUID;
      imageMap: ImageMap;
      name: string;
    }
  ): Promise<WithContext<Blob, DocxSerializer.DocumentContext>> {
    const { dbComments, documentAuthors, documentId, imageMap, name } = options;
    const { authorNames } = Queries.getTrackedChangesAuthorInfo(editor);
    const { context, value: docX } = DocxSerializer.createDocxDocument(editor.children, {
      alineDocumentId: documentId,
      authorNames: new Map(
        [...documentAuthors, ...authorNames].map(userOrName => {
          const key = typeof userOrName === 'string' ? userOrName : userOrName.id;
          const authorName = typeof userOrName === 'string' ? userOrName : userOrName.displayName;
          return [key, authorName];
        })
      ),
      dbComments,
      docProperties: { title: name },
      imageMap: imageMap,
    });
    const value = await Packer.toBlob(docX);

    return { context, value };
  },
};
